import React, {useCallback, useRef, useState, useEffect} from 'react';
import {ModelDTO} from 'hemwb-api/dist/models/ModelDTO';
import {modelFileItemDownload, useModelFileTH, useModelFileTreeLoader} from '../../../../../store/modelFile';
import {useMounted} from '../../../../../hooks/useMounted';
import {useDispatch} from 'react-redux';
import {InformationClassificationIcon} from '../../../../icons/classification/InformationClassificationIcon';
import {Button, Checkbox, Tabs, Typography} from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import {tid} from '../../../../../testUtils';
import {ApiResponse} from 'hemwb-api';
import {downloadFromResponse} from '../../../../../utils/download';
import {isModelExternal, isModelTheorem, isModelGlobal, modelDownload} from '../../../../../store/model';
import {trackModelDownloaded, trackModelItemDownloaded} from '../../../../../tracking/tracking';
import {defaultLegalNotice} from '../../../../core/defaultLegalNotice';
import DownloadFileTreeTH from '../../../../core/FileTree/DownloadFileTreeTH';
import FileTree from '../../../../core/FileTree/FileTree';
import {useCurrentUser} from '../../../../../store/auth';

const {Text, Title} = Typography;

type ModelDownloadTHProps = {
  model: ModelDTO;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const ModelDownloadTH: React.FC<ModelDownloadTHProps> = ({model, onSuccess, onCancel}) => {
  const dispatch = useDispatch();
  const [downloading, setDownloading] = useState<string>();
  const [confirmed, setConfirmed] = useState(false);
  const [validate, setValidate] = useState(false);
  const [tab, setTab] = useState('theorem');
  const {files} = useModelFileTH(model);
  const externalModel = isModelExternal(model);
  const theoremModel = isModelTheorem(model);
  const globalModel = isModelGlobal(model);
  const {id: currentUserId} = useCurrentUser();
  const mounted = useMounted();
  const cancelled = useRef(false);
  const {data: supportingDocs, load: loadFiles} = useModelFileTreeLoader(model.uuid);

  useEffect(() => {
    if (tab === 'theorem') {
      loadFiles();
    }
  }, [tab, loadFiles]);
  const downloadIfNotCancelled = useCallback(
    (response: ApiResponse<Blob>) => (cancelled.current ? Promise.reject() : downloadFromResponse(response.raw)),
    [],
  );
  const [downloadAll, setDownloadAll] = useState<boolean>(false);

  const handleCancel = useCallback(() => {
    cancelled.current = true;
    onCancel?.();
  }, [onCancel]);

  const handleDownloadAllButton = useCallback(() => {
    setConfirmed(!confirmed);
    let isAllowDownloadAll = false;
    if (files) {
      files.specification.forEach((file) => {
        if (globalModel || file.cdd === 'No' || (file.userAccess && file.cdd === 'Yes' && !file.isPrivate)) {
          isAllowDownloadAll = true;
        }
        return downloadAll;
      });
    }
    setDownloadAll(isAllowDownloadAll);
  }, [files, confirmed, downloadAll, globalModel]);

  const handleDownloadItem = useCallback(
    (itemUuid: string, itemName: string) => {
      setDownloading(itemUuid);
      cancelled.current = false;
      modelFileItemDownload(dispatch, model.uuid, itemUuid)
        .then(downloadIfNotCancelled)
        .then(() => {
          trackModelItemDownloaded(model, itemName, itemUuid);
        })
        .catch(() => null)
        .finally(() => {
          mounted.current && setDownloading(undefined);
        });
    },
    [dispatch, downloadIfNotCancelled, model, mounted],
  );

  const handleDownloadAllSupportingDocs = useCallback(() => {
    setDownloading('all');

    modelDownload(dispatch, model.uuid)
      .then(downloadIfNotCancelled)
      .then(() => {
        trackModelDownloaded(model);
        onSuccess?.();
      })
      .catch(() => null)
      .finally(() => {
        mounted.current && setDownloading(undefined);
      });
  }, [dispatch, downloadIfNotCancelled, model, mounted, onSuccess]);

  const handleDownloadAllTheoremFiles = useCallback(() => {
    setDownloading('all');
    if (files && Array.isArray(files.specification)) {
      modelDownload(dispatch, model.uuid)
        .then(downloadIfNotCancelled)
        .then(() => {
          trackModelDownloaded(model);
          onSuccess?.();
        })
        .catch(() => null)
        .finally(() => {
          mounted.current && setDownloading(undefined);
        });
    }
  }, [dispatch, downloadIfNotCancelled, model, mounted, onSuccess, files]);

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
      {/* Header */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <DownloadOutlined style={{fontSize: '22px', color: '#262626'}} />
          <Title level={4} style={{margin: 0, fontSize: '20px', fontWeight: 600}}>
            Download Model
          </Title>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
          <InformationClassificationIcon
            classification={model.metadata.information_classification}
            style={{width: 24, height: 24}}
          />
          <Text style={{fontSize: '14px', fontWeight: 500}}>{model.metadata.information_classification}</Text>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={tab}
        onChange={(newTab) => setTab(newTab)}
        items={[
          {key: 'theorem', label: 'THEOREM files'},
          {key: 'supporting', label: 'Supporting Documents'},
        ]}
        style={{marginBottom: 0}}
      />

      {/* File trees */}
      {tab === 'theorem' ? (
        files ? (
          <DownloadFileTreeTH
            files={files}
            externalModel={externalModel}
            handleDownload={handleDownloadItem}
            isModelGlobal={globalModel}
            renderButtons={(file) => (
              <Button
                key={`download-item-${file.uuid}`}
                size="small"
                type="primary"
                icon={<DownloadOutlined />}
                disabled={
                  (!!downloading || !confirmed) &&
                  (!file.isPrivate || (file.isPrivate && file.owner.isid === currentUserId))
                }
                loading={downloading === file.uuid}
                onClick={() => handleDownloadItem(file.uuid, file.fileName)}
                {...tid('download-item', file.fileName)}
              >
                Download
              </Button>
            )}
          />
        ) : null
      ) : supportingDocs ? (
        <FileTree
          files={supportingDocs}
          theoremModel={theoremModel}
          renderButtons={(node) => (
            <Button
              key={`download-item-file-${node.uuid}`}
              size="small"
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!!downloading || !validate}
              loading={downloading === node.uuid}
              onClick={() => handleDownloadItem(node.uuid, node.name)}
              {...tid('download-item-file', node.name)}
            >
              Download
            </Button>
          )}
        />
      ) : null}

      {/* Legal notice */}
      <Text style={{fontSize: '14px', color: '#262626', lineHeight: '1.6'}}>
        {model.metadata.legal_notice || defaultLegalNotice}
      </Text>

      {/* Checkbox — separate per tab */}
      {tab === 'theorem' ? (
        <div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
          <Checkbox
            {...tid('checkbox-confirm-file')}
            checked={confirmed}
            onChange={handleDownloadAllButton}
            style={{marginTop: '2px', flexShrink: 0}}
          />
          <span style={{cursor: 'pointer', fontSize: '14px'}} onClick={handleDownloadAllButton}>
            I have read the legal notice
          </span>
        </div>
      ) : (
        <div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
          <Checkbox
            {...tid('checkbox-confirm')}
            checked={validate}
            onChange={() => setValidate(!validate)}
            style={{marginTop: '2px', flexShrink: 0}}
          />
          <span style={{cursor: 'pointer', fontSize: '14px'}} onClick={() => setValidate(!validate)}>
            I have read the legal notice
          </span>
        </div>
      )}

      {/* Footer */}
      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px'}}>
        <Button onClick={handleCancel} disabled={!!downloading}>
          Cancel
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading === 'all'}
          disabled={tab === 'theorem' ? !downloadAll || !confirmed : !validate}
          onClick={tab === 'theorem' ? handleDownloadAllTheoremFiles : handleDownloadAllSupportingDocs}
        >
          Download all
        </Button>
      </div>
    </div>
  );
};

export default ModelDownloadTH;
